import {
    ChangeDetectionStrategy,
    Component,
    EventEmitter,
    Input,
    OnChanges,
    OnInit,
    Output,
    SimpleChanges,
    ViewEncapsulation
} from '@angular/core';
import { ExtendedBaseTab } from '@common/classes/extended-base-tab';
import { CodelistDisplayModel } from '@common/models/common/CodelistDisplay.model';
import { marker } from '@common/translate-marker';
import { parsePcsApprovalRecipient } from '@common/utils/pcs-approval-recipient.util';
import { DialogCloseResult } from '@progress/kendo-angular-dialog';
import { PcsDocumentAndApprovalServices } from '@services/pcs-document-and-approval-services.service';
import { CustomExtendedTabService } from 'app/singleton-services/custom-extended-tab.service';
import { IPcsDocumentOrApprovalDetailsDisplayModel } from 'app/vessel-visit/vessel-visit-list/models/PcdDocumentAndApprovalModels/DetailsDisplayModels/IPcsDocumentOrApprovalDetailsDisplayModel';
import { PcsDocumentAndApprovalActionStatusChangesDisplayModel } from '../../../../../vessel-visit/vessel-visit-list/models/PcdDocumentAndApprovalModels/DetailsDisplayModels/PcsDocumentAndApprovalActionStatusChangesDisplayModel';
import { PcsDocumentAndApprovalAllPossibleStateTransitionsDisplayModel } from '../../../../../vessel-visit/vessel-visit-list/models/PcdDocumentAndApprovalModels/OtherDisplayModels/PcsDocumentAndApprovalAllPossibleStateTransitionsDisplayModel';
import { PcsDocumentOrApprovalLocation } from '../../models/PcsDocumentOrApprovalLocation';

@Component({
    selector: 'app-pcs-approval-tab-actions-status-changes',
    templateUrl: './pcs-approval-tab-actions-status-changes.component.html',
    styleUrls: ['./pcs-approval-tab-actions-status-changes.component.scss'],
    encapsulation: ViewEncapsulation.None,
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
export class PcsApprovalTabActionsStatusChangesComponent extends ExtendedBaseTab implements OnInit, OnChanges {
    @Input() actionStatusChanges: PcsDocumentAndApprovalActionStatusChangesDisplayModel[];
    @Input() isForApproval = false;
    @Input() approvalOrDocumentId = -1;
    @Input() allowedStatus = false;
    @Input() generalActStatusId = null;
    @Input() showApproveButtons: boolean = true;

    // used to check if there are multiple delivery order approvals for a different confirmation message on approve :)
    @Input() multipleDeliveryOrderApprovals: boolean;

    // for service request needed only
    @Input() approvalLocation: PcsDocumentOrApprovalLocation;
    @Input() serviceRequestVersion: number;

    @Output() changesToPcsDocumentOrApproval: EventEmitter<IPcsDocumentOrApprovalDetailsDisplayModel> =
        new EventEmitter<IPcsDocumentOrApprovalDetailsDisplayModel>();

    possibleStateChanges: PcsDocumentAndApprovalAllPossibleStateTransitionsDisplayModel[];
    showOrganizationSelection = false;
    possibleOrganizationSelections: CodelistDisplayModel[];
    currentSelection: PcsDocumentAndApprovalAllPossibleStateTransitionsDisplayModel = null;
    selectedOrganization: string | number | null;
    selectedTerminal: string | null;
    canSendApproval: boolean;
    canComplete: boolean;
    canApprove: boolean;
    canReject: boolean;
    canDelete: boolean;
    canOnHold: boolean;
    canSend2ndApproval: boolean;
    canSend3rdApproval: boolean;
    showChangeOrganization = false;
    // rejection remark variables
    rejectionRemarkDialogOpened = false;
    rejectionRemarkChanges = false;
    remarkValue = '';

    sendApprovalDefaultOrganization: CodelistDisplayModel | null;

    constructor(
        customExtendedTabService: CustomExtendedTabService,
        private service: PcsDocumentAndApprovalServices
    ) {
        super(customExtendedTabService);
    }

    ngOnInit(): void {
        this.defaultPaging();
        this.loadPossibleStateTransitions();
        this.loadItems(this.actionStatusChanges);
    }

    ngOnChanges(changes: SimpleChanges): void {
        if (changes && changes.actionStatusChanges && !changes.actionStatusChanges.firstChange) {
            this.loadItems(this.actionStatusChanges);
        }
    }

    public closeNewApprovalModal(): void {
        this.resetStatusChangeDialogState();
    }

    public changeCurrentStateTransition($event): void {
        if (!$event) {
            this.currentSelection = null;
            return;
        }
        this.currentSelection = $event;
        if (this.currentSelection.toStatus.id.includes('WFA')) {
            this.showOrganizationSelection = true;
            this.possibleOrganizationSelections = this.currentSelection.possibleOrganizationsForApproval;
        } else {
            this.showOrganizationSelection = false;
            this.possibleOrganizationSelections = [];
        }
    }

    public newStateTransition(): void {
        if (this.currentSelection) {
            // the confirm opens in the app-level dialog container, which renders under this inline dialog
            this.showChangeOrganization = false;
            this.openStatusChangeConfirm();
        }
    }

    public closeConfirmDialog() {
        this.resetStatusChangeDialogState();
    }

    private submitConfirmDialog() {
        if (this.currentSelection === null || (this.selectedOrganization === null && this.showOrganizationSelection))
            return;

        if (this.rejectionRemarkChanges && this.remarkValue !== '') {
            this.service
                .newRemark({
                    isForApproval: this.isForApproval,
                    pcsDocumentOrApprovalId: this.approvalOrDocumentId,
                    remark: this.remarkValue
                })
                .subscribe((response) => {
                    this.submitActionStatusChange();
                });
        } else this.submitActionStatusChange();
    }

    public changeSelectedOrganization($event) {
        const recipient = parsePcsApprovalRecipient($event);
        this.selectedOrganization = recipient.forOrganization;
        this.selectedTerminal = recipient.terminalId;
    }

    public searchOrganizations(filter: string) {
        this.possibleOrganizationSelections = this.currentSelection.possibleOrganizationsForApproval.filter(
            (org) => org.name.toLowerCase().indexOf(filter.toLowerCase()) !== -1
        );
    }

    checkVisibility(array) {
        this.canApprove = false;
        this.canComplete = false;
        this.canDelete = false;
        this.canOnHold = false;
        this.canReject = false;
        this.canSendApproval = false;
        this.canSend2ndApproval = false;
        this.canSend3rdApproval = false;
        array.forEach((item) => {
            if (item.toStatus.id === 'WFA') {
                this.canSendApproval = true;
            }
            if (item.toStatus.id === 'NFA') {
                this.canComplete = true;
            }
            if (item.toStatus.id === 'APP') {
                this.canApprove = true;
            }
            if (item.toStatus.id === 'REJ') {
                this.canReject = true;
            }
            if (item.toStatus.id === 'DEL') {
                this.canDelete = true;
            }
            if (item.toStatus.id === 'ONH') {
                this.canOnHold = true;
            }
            if (item.toStatus.id === '2ND_WFA') {
                this.canSend2ndApproval = true;
            }
            if (item.toStatus.id === '3RD_WFA') {
                this.canSend3rdApproval = true;
            }
        });
    }

    changeStatus(action) {
        if (action === 'complete') {
            this.currentSelection = this.possibleStateChanges.find((item) => item.toStatus.id === 'NFA');
        }
        if (action === 'approve') {
            this.currentSelection = this.possibleStateChanges.find((item) => item.toStatus.id === 'APP');
            // this is to save the organization and terminal that were selected for approval in the database for the future use(email event)
            // currently it is only saved when the status is changed from CRT to WFA
            this.selectedOrganization = this.actionStatusChanges?.find(
                (item) => item?.pcsDocumentAndApprovalToStatus?.id === 'WFA'
            )?.forOrganization?.id;
            this.selectedTerminal = this.actionStatusChanges?.find(
                (item) => item?.pcsDocumentAndApprovalToStatus?.id === 'WFA'
            )?.terminalId;
        }
        if (action === 'delete') {
            this.currentSelection = this.possibleStateChanges.find((item) => item.toStatus.id === 'DEL');
        }
        if (action === 'sendApproval') {
            this.currentSelection = this.possibleStateChanges.find((item) => item.toStatus.id === 'WFA');

            if (
                this.currentSelection.defaultOrganizationsForApproval &&
                this.currentSelection.defaultOrganizationsForApproval.length === 1
            ) {
                this.sendApprovalDefaultOrganization = this.currentSelection.defaultOrganizationsForApproval[0];
                this.changeSelectedOrganization(this.sendApprovalDefaultOrganization);
            }

            this.changeCurrentStateTransition(this.currentSelection);
            this.showChangeOrganization = true;
        }
        if (action === 'reject') {
            this.currentSelection = this.possibleStateChanges.find((item) => item.toStatus.id === 'REJ');
            // this is to save the organization and terminal that were selected for approval in the database for the future use(email event)
            // currently it is only saved when the status is changed from CRT to WFA
            this.selectedOrganization = this.actionStatusChanges?.find(
                (item) => item?.pcsDocumentAndApprovalToStatus?.id === 'WFA'
            )?.forOrganization?.id;
            this.selectedTerminal = this.actionStatusChanges?.find(
                (item) => item?.pcsDocumentAndApprovalToStatus?.id === 'WFA'
            )?.terminalId;
        }
        if (action === 'onHold') {
            this.currentSelection = this.possibleStateChanges.find((item) => item.toStatus.id === 'ONH');
        }
        if (action === 'send2ndApproval') {
            this.currentSelection = this.possibleStateChanges.find((item) => item.toStatus.id === '2ND_WFA');
            this.changeCurrentStateTransition(this.currentSelection);
            this.showChangeOrganization = true;
        }
        if (action === 'send3rdApproval') {
            this.currentSelection = this.possibleStateChanges.find((item) => item.toStatus.id === '3RD_WFA');
            this.changeCurrentStateTransition(this.currentSelection);
            this.showChangeOrganization = true;
        }
        if (this.currentSelection && !this.showChangeOrganization) {
            this.openStatusChangeConfirm();
        }
    }

    public openRejectionRemarkDialog() {
        this.rejectionRemarkDialogOpened = true;
    }

    public closeRejectionRemarksDialog() {
        this.resetStatusChangeDialogState();
    }

    public submitRejectionRemark() {
        this.rejectionRemarkChanges = true;
        this.rejectionRemarkDialogOpened = false;
        this.changeStatus('reject');
    }

    private loadPossibleStateTransitions() {
        this.service
            .getAllPossibleStateTransitions({
                isForApproval: this.isForApproval,
                documentOrApprovalId: this.approvalOrDocumentId
            })
            .subscribe((response) => {
                this.possibleStateChanges = response;
                this.checkVisibility(this.possibleStateChanges);
            });
    }

    private submitActionStatusChange() {
        this.service
            .newActionStatusChange({
                pcsDocumentOrApprovalId: this.approvalOrDocumentId,
                statusChangeId: this.currentSelection.id,
                isForApproval: this.isForApproval,
                forOrganization: parsePcsApprovalRecipient(this.selectedOrganization).forOrganization,
                terminalId: this.selectedTerminal,
                serviceRequestVersion:
                    ['APP', 'REJ'].includes(this.currentSelection.toStatus.id) &&
                    this.approvalLocation === PcsDocumentOrApprovalLocation.SERVICE_REQUEST
                        ? this.serviceRequestVersion
                        : null
            })
            .subscribe({
                next: (response) => {
                    this.actionStatusChanges = response.actionStatusChanges;
                    this.loadItems(this.actionStatusChanges);
                    this.loadPossibleStateTransitions();
                    this.checkVisibility(this.possibleStateChanges);
                    this.changesToPcsDocumentOrApproval.emit(response);
                    this.resetStatusChangeDialogState();
                },
                error: (err) => {
                    if (err.error?.errorMessage?.includes('version mismatch')) {
                        setTimeout(() => {
                            window.location.reload();
                        }, 3000);
                    }
                }
            });
    }

    private openStatusChangeConfirm(): void {
        if (!this.currentSelection) {
            return;
        }

        this.dialogService
            .confirm(
                this.getStatusChangeConfirmTitle(),
                this.getStatusChangeConfirmMessage(),
                this.translateService.instant(marker('Submit')),
                null,
                this.translateService.instant(marker('Cancel'))
            )
            .subscribe((result) => {
                if (result === true) {
                    this.submitConfirmDialog();
                    return;
                }

                if (result === false || result instanceof DialogCloseResult) {
                    this.closeConfirmDialog();
                }
            });
    }

    private getStatusChangeConfirmTitle(): string {
        return this.translateService.instant(
            marker(
                this.multipleDeliveryOrderApprovals && this.currentSelection?.toStatus?.id === 'APP'
                    ? 'Approve'
                    : 'Confirm change status'
            )
        );
    }

    private getStatusChangeConfirmMessage(): string {
        if (this.multipleDeliveryOrderApprovals && this.currentSelection?.toStatus?.id === 'APP') {
            return this.translateService.instant(
                marker(
                    'There are other pending approvals for this delivery order. Do you still want to proceed with approval?'
                )
            );
        }

        const fromStatusName = this.currentSelection?.fromStatus?.name
            ? this.translateService.instant(this.currentSelection.fromStatus.name)
            : '';
        const toStatusName = this.currentSelection?.toStatus?.name
            ? this.translateService.instant(this.currentSelection.toStatus.name)
            : '';

        return `${this.translateService.instant(
            marker('Are you sure you want to change status from')
        )} ${fromStatusName} ${this.translateService.instant(marker('to'))} ${toStatusName}?`;
    }

    public closeStatusChangeDialogs(): void {
        this.resetStatusChangeDialogState();
    }

    private resetStatusChangeDialogState(): void {
        this.currentSelection = null;
        this.possibleOrganizationSelections = [];
        this.showOrganizationSelection = false;
        this.showChangeOrganization = false;
        this.selectedOrganization = null;
        this.selectedTerminal = null;
        this.sendApprovalDefaultOrganization = null;
        this.rejectionRemarkDialogOpened = false;
        this.rejectionRemarkChanges = false;
        this.remarkValue = '';
    }
}
